import type {
  QuoteDetailPublic,
  QuoteTaskChargePublic,
  QuoteTaskDetailPublic,
  Workscope,
} from "@common/client"
import { toNumberOrNull } from "@common/lib/number"
import { formatMoney } from "@/routes/_layout/quoting_/quotes/-lib/charge-math"
import { totalCharges } from "@/routes/_layout/quoting_/quotes/builder/$quoteId/-common/lib/task-form"
import { kindLabel } from "./traveller"

export const WORKSCOPES = [
  "test",
  "repair",
  "overhaul",
] as const satisfies readonly Workscope[]

export type PricedWorkscope = (typeof WORKSCOPES)[number]

export function isPartsQuote(quote: QuoteDetailPublic): boolean {
  return quote.kind === "rfp"
}

export function partTitle(task: QuoteTaskDetailPublic): string {
  return [
    task.part?.number ?? task.manual_reference,
    task.part?.description ?? task.description,
  ]
    .filter(Boolean)
    .join(" · ")
}

export function taskTitle(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
): string {
  return isPartsQuote(quote) ? partTitle(task) : (task.description ?? "")
}

export function workscopeCharge(
  task: QuoteTaskDetailPublic,
  kind: PricedWorkscope,
): QuoteTaskChargePublic | null {
  return (
    task.active_charges.find(
      (charge) => charge.type === "estimate" && charge.estimate_kind === kind,
    ) ?? null
  )
}

export function notOffered(charge: QuoteTaskChargePublic | null): boolean {
  return charge !== null && toNumberOrNull(charge.sale_unit_price) === 0
}

export function workscopePriced(charge: QuoteTaskChargePublic | null): boolean {
  if (charge === null) return false
  if (notOffered(charge)) return true
  const price = toNumberOrNull(charge.sale_unit_price) ?? 0
  return price > 0 && (charge.lead_time ?? 0) > 0
}

export function workscopesDone(task: QuoteTaskDetailPublic): number {
  return WORKSCOPES.filter((kind) =>
    workscopePriced(workscopeCharge(task, kind)),
  ).length
}

export function offeredCharges(
  task: QuoteTaskDetailPublic,
): QuoteTaskChargePublic[] {
  return WORKSCOPES.flatMap((kind) => {
    const charge = workscopeCharge(task, kind)
    return charge && !notOffered(charge) ? [charge] : []
  })
}

export function offerSummary(task: QuoteTaskDetailPublic): string {
  return offeredCharges(task)
    .map((charge) =>
      [
        charge.estimate_kind ? kindLabel(charge.estimate_kind) : "",
        formatMoney(
          toNumberOrNull(charge.sale_unit_price),
          charge.currency ?? "none",
        ),
      ].join(" "),
    )
    .join(" · ")
}

export function highestOffer(
  task: QuoteTaskDetailPublic,
): QuoteTaskChargePublic | null {
  return offeredCharges(task).reduce<QuoteTaskChargePublic | null>(
    (best, charge) =>
      best === null ||
      (toNumberOrNull(charge.sale_unit_price) ?? 0) >
        (toNumberOrNull(best.sale_unit_price) ?? 0)
        ? charge
        : best,
    null,
  )
}

export function billableCharges(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
): QuoteTaskChargePublic[] {
  return isPartsQuote(quote)
    ? task.active_charges.filter(
        (charge) =>
          charge.type !== "estimate" ||
          charge.estimate_kind === task.awarded_kind,
      )
    : totalCharges(task)
}
